import { useState } from 'react'

import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import { DEFAULT_AVATAR_NAME } from 'app/constants/avatar'
import { Tooltip } from 'app/components'
import styles from './styles.module.scss'
import { getNormalizedUrl } from 'app/utils/links'

const getOperators = ({ widgetData }) => widgetData.widgetInfo.data.operators

const getHashOfString = (str) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  hash = Math.abs(hash)
  return hash
}

const normalizeHash = (hash, min, max) => {
  return Math.floor((hash % (max - min)) + min)
}

const hRange = [50, 300]
const sRange = [10, 87]
const lRange = [60, 85]

const generateHSL = (name) => {
  const hash = getHashOfString(name)
  const h = normalizeHash(hash, hRange[0], hRange[1])
  const s = normalizeHash(hash, sRange[0], sRange[1])
  const l = normalizeHash(hash, lRange[0], lRange[1])
  return [h, s, l]
}

const HSLtoString = (hsl) => {
  return `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`
}

// direction will be set to top if it is not specified
const Avatar = ({
  operatorId,
  url = '',
  alt = '',
  withBorder = true,
  tooltipText,
  tooltipDirection = 'top',
  backgroundSize = 32,
  tooltipDistanceFromAvatar = 30,
  showTooltip = true,
  onlineBadge = false,
  avatarInfo,
  borderRadius = '50%',
}) => {
  const [hasError, setHasError] = useState(false)
  const customCssRules = { '--raychat-avatar-size': backgroundSize }
  const operators = useSelector(getOperators)

  const agent = operatorId && operators.find((item) => item.id === operatorId)
  const avatarName = agent ? agent.name : avatarInfo ? avatarInfo.name : ''
  const avatarUrl = agent ? agent.avatar : avatarInfo ? avatarInfo.url : url ? url : ''
  const isAvatarUrlDefault = avatarUrl?.includes(DEFAULT_AVATAR_NAME)
  const bgStyle =
    avatarName && (!avatarUrl || isAvatarUrlDefault || hasError)
      ? { backgroundColor: HSLtoString(generateHSL(avatarName)) }
      : {}

  const getAvatarUrl = () => {
    if (isAvatarUrlDefault) return ''

    const path = url || avatarUrl

    if (path) return getNormalizedUrl(path)

    return ''
  }

  const showAvatarImage = !hasError && !!getAvatarUrl()

  const getTooltipText = () => {
    if (!showTooltip) return ''

    if (tooltipText) return tooltipText

    return avatarName
  }

  const getAltText = () => {
    if (alt) return alt

    return avatarName
  }

  const getPlaceholderText = () => {
    if (avatarName && (!avatarUrl || isAvatarUrlDefault || hasError)) {
      const splittedName = avatarName.split(' ')

      const firstLetter = splittedName[0]?.[0]
      const secondLetter = splittedName[1]?.[0]

      return secondLetter ? `${firstLetter} ${secondLetter}` : firstLetter
    }

    return ''
  }

  return (
    <Tooltip distance={tooltipDistanceFromAvatar} content={getTooltipText()} direction={tooltipDirection}>
      <div data-badge={onlineBadge} className={styles.avatar_wrapper_outer}>
        <div
          style={customCssRules}
          className={clsx(
            styles.avatar_wrapper_outer_wrapper_container,
            hasError && styles.avatar_hasError,
            withBorder && styles.avatar_withBorder,
          )}
        >
          <div
            style={{ ...bgStyle, borderRadius }}
            data-content={getPlaceholderText()}
            className={styles.avatar_wrapper}
          >
            {showAvatarImage && (
              <img
                alt={getAltText()}
                className={styles.avatar}
                src={getAvatarUrl()}
                onError={() => setHasError(true)}
              />
            )}
          </div>
        </div>
      </div>
    </Tooltip>
  )
}

Avatar.propTypes = {
  url: PropTypes.string,
  alt: PropTypes.string,
  withBorder: PropTypes.bool,
  backgroundSize: PropTypes.number,
  tooltipDirection: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  tooltipDistanceFromAvatar: PropTypes.number,
  tooltipText: PropTypes.string,
  onlineBadge: PropTypes.bool,
}

export default Avatar
